import React, { useEffect, useMemo, useRef } from 'react';
import styles from '../styles/Live.module.css';
import { seriesColor } from '../lib/palette';

// Retired / stopped cars go flat so they read as out of the fight; every
// car still running keeps its team colour on the map.
const RETIRED_FILL = '#48484d';

/**
 * LiveTrackMap
 *
 * Renders a circuit outline with driver dots that ALWAYS stay on the
 * racing line. Instead of linearly interpolating raw (x, y) between
 * snapshots (which cuts across tight corners), we:
 *
 *   1. Pre-compute a cumulative-arclength index over the track polyline.
 *   2. Project each incoming raw position onto the nearest point on the
 *      polyline, converting it to an arclength s ∈ [0, totalLen).
 *   3. Animate each driver's current s toward its latest target with
 *      requestAnimationFrame, taking the SHORT direction (handles
 *      start/finish wraparound), and render the dot at arclengthToXY(s).
 *
 * Direct DOM mutation (circle.setAttribute) is used in the RAF loop so
 * the 60 Hz animation doesn't thrash React's reconciler.
 */

/* ─── Geometry helpers ──────────────────────────────────────────────── */

function rotate(x, y, angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return [c * x - s * y, s * x + c * y];
}

/**
 * Build a cumulative-arclength index over a closed polyline.
 * Returns { points: [[x,y], ...], cumsum: Float64Array, totalLen: number }
 * where cumsum[i] is the arclength from points[0] to points[i], and the
 * last segment implicitly closes the loop (points[N-1] → points[0]).
 */
function buildArclengthIndex(outline) {
  if (!outline || outline.length < 2) return null;
  const n = outline.length;
  const cumsum = new Float64Array(n + 1); // last slot is totalLen
  cumsum[0] = 0;
  let total = 0;
  for (let i = 1; i < n; i++) {
    const dx = outline[i][0] - outline[i - 1][0];
    const dy = outline[i][1] - outline[i - 1][1];
    total += Math.hypot(dx, dy);
    cumsum[i] = total;
  }
  // closing segment
  const dxc = outline[0][0] - outline[n - 1][0];
  const dyc = outline[0][1] - outline[n - 1][1];
  total += Math.hypot(dxc, dyc);
  cumsum[n] = total;
  return { points: outline, cumsum, totalLen: total };
}

/**
 * Find the nearest point on the polyline to (x, y) and return its
 * arclength along the track. O(N) scan per call — fine at snapshot rate.
 */
function projectToArclength(index, x, y) {
  const { points, cumsum } = index;
  const n = points.length;
  let bestDist2 = Infinity;
  let bestArc = 0;

  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const segLen2 = dx * dx + dy * dy;
    if (segLen2 === 0) continue;

    // Parametric t of projection of (x,y) onto segment a→b, clamped to [0,1]
    let t = ((x - a[0]) * dx + (y - a[1]) * dy) / segLen2;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;

    const px = a[0] + t * dx;
    const py = a[1] + t * dy;
    const ddx = x - px;
    const ddy = y - py;
    const d2 = ddx * ddx + ddy * ddy;

    if (d2 < bestDist2) {
      bestDist2 = d2;
      // arclength at segment start + t * segLen
      bestArc = cumsum[i] + t * Math.sqrt(segLen2);
    }
  }
  return bestArc;
}

/**
 * Convert an arclength s ∈ ℝ back to (x, y) on the polyline, wrapping at
 * totalLen. Binary searches cumsum for the segment containing s.
 */
function arclengthToXY(index, s) {
  const { points, cumsum, totalLen } = index;
  const n = points.length;

  // wrap s into [0, totalLen)
  s = ((s % totalLen) + totalLen) % totalLen;

  // binary search: smallest i such that cumsum[i+1] > s
  let lo = 0;
  let hi = n; // cumsum has n+1 entries, last = totalLen
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cumsum[mid + 1] <= s) lo = mid + 1;
    else hi = mid;
  }
  const i = Math.min(lo, n - 1);
  const a = points[i];
  const b = points[(i + 1) % n];
  const segStart = cumsum[i];
  const segEnd = cumsum[i + 1];
  const segLen = segEnd - segStart;
  const t = segLen > 0 ? (s - segStart) / segLen : 0;
  return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])];
}

/**
 * Interpolate along arclength taking the SHORT direction around the loop.
 * Avoids going 'the long way' when a car crosses the start/finish line.
 */
function arcLerp(from, to, alpha, totalLen) {
  let diff = to - from;
  if (diff > totalLen / 2) diff -= totalLen;
  else if (diff < -totalLen / 2) diff += totalLen;
  let r = from + diff * alpha;
  return ((r % totalLen) + totalLen) % totalLen;
}

/**
 * Continue past the newest sample at the speed the last two samples imply.
 * `alpha > 1` in arcLerp runs on past `to` along the same shortest-way
 * arc, which is exactly the motion we want — and it still wraps correctly
 * across start/finish.
 */
function extrapolateArc(buf, renderTime, totalLen) {
  const last = buf[buf.length - 1];
  const over = renderTime - last.t;
  if (over > EXTRAPOLATE_MAX_MS || buf.length < 2) return last.arc;
  const prev = buf[buf.length - 2];
  const dt = last.t - prev.t;
  if (dt <= 0) return last.arc;
  return arcLerp(prev.arc, last.arc, 1 + over / dt, totalLen);
}

/* ─── Main component ─────────────────────────────────────────────────── */

// Interpolation buffer knobs.
// RENDER_LAG_MS    How far behind the newest sample we render. Higher =
//                  smoother motion (there's always a newer sample in the
//                  buffer to interpolate toward) but more visual lag.
//                  700 ms is invisible vs F1's ~30 s broadcast delay.
//                  A snapshot that takes longer than this to arrive used
//                  to push the render clock past the end of the buffer,
//                  where the code clamped and the dots froze until the
//                  next push — which is what "the replay keeps pausing"
//                  looked like. It now extrapolates instead.
// EXTRAPOLATE_MAX_MS  How far past the newest sample we will keep
//                  inventing motion. Past this the snapshot really is
//                  late and holding is honest; the next one re-syncs.
// BUFFER_MAX       Max samples retained per driver. Enough to cover the
//                  whole pos_history the backend sends (30) plus some
//                  carryover from previous snapshots.
const RENDER_LAG_MS = 700;
const BUFFER_MAX = 40;
const EXTRAPOLATE_MAX_MS = 1500;

export default function LiveTrackMap({ outline, corners, rotation, drivers, selected, hovered, onToggle, onHover }) {
  const rot = rotation ?? 0;

  // Rotate + y-flip the outline once per outline/rotation change.
  const outlineXY = useMemo(() => {
    if (!outline || outline.length === 0) return [];
    return outline.map(([x, y]) => {
      const [rx, ry] = rotate(x, y, rot);
      return [rx, -ry];
    });
  }, [outline, rot]);

  const cornerPts = useMemo(() => {
    if (!corners) return [];
    return corners.map(c => {
      const [rx, ry] = rotate(c.x, c.y, rot);
      return { number: c.number, x: rx, y: -ry };
    });
  }, [corners, rot]);

  // Arclength index, rebuilt when the outline changes.
  const arcIndex = useMemo(() => buildArclengthIndex(outlineXY), [outlineXY]);

  // ── viewBox + path from rotated outline ──────────────────────────────
  //
  // Track bbox = tight bbox of the outline + corners.
  // Label frame bbox = track bbox padded outward so labels can sit on its
  // perimeter without touching the track.
  // viewBox = label frame bbox padded further so label text doesn't clip
  // at the edges.
  const trackBbox = useMemo(() => {
    if (outlineXY.length === 0) return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of outlineXY) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    for (const c of cornerPts) {
      if (c.x < minX) minX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x;
      if (c.y > maxY) maxY = c.y;
    }
    return { minX, minY, maxX, maxY };
  }, [outlineXY, cornerPts]);

  const dotRadius = useMemo(() => {
    const w = trackBbox.maxX - trackBbox.minX;
    const h = trackBbox.maxY - trackBbox.minY;
    return Math.max(80, Math.max(w, h) * 0.015);
  }, [trackBbox]);

  const labelFrame = useMemo(() => {
    const { minX, minY, maxX, maxY } = trackBbox;
    const dim = Math.max(maxX - minX, maxY - minY);
    // Push labels outward from the track by a generous margin. dotRadius
    // scales with dim so this stays proportional.
    const margin = dim * 0.10 + dotRadius * 4;
    return {
      minX: minX - margin,
      minY: minY - margin,
      maxX: maxX + margin,
      maxY: maxY + margin,
    };
  }, [trackBbox, dotRadius]);

  const viewBox = useMemo(() => {
    const { minX, minY, maxX, maxY } = labelFrame;
    // extra outer padding for the label text itself (anchored at center,
    // extends outward ~half a label width on each side)
    const textPad = dotRadius * 4;
    const vMinX = minX - textPad;
    const vMinY = minY - textPad;
    const w = (maxX - minX) + textPad * 2;
    const h = (maxY - minY) + textPad * 2;
    return `${vMinX} ${vMinY} ${w} ${h}`;
  }, [labelFrame, dotRadius]);

  const pathD = useMemo(() => {
    if (outlineXY.length === 0) return '';
    let d = `M ${outlineXY[0][0]} ${outlineXY[0][1]}`;
    for (let i = 1; i < outlineXY.length; i++) {
      d += ` L ${outlineXY[i][0]} ${outlineXY[i][1]}`;
    }
    return d + ' Z';
  }, [outlineXY]);

  // Start/finish line — drawn perpendicular to the track tangent at
  // outline[0]. FastF1's lap.get_pos_data() starts at the start/finish
  // line for a race lap, which is how track.py builds the outline. We
  // just need the tangent at that point to know the perpendicular.
  const startFinish = useMemo(() => {
    if (outlineXY.length < 2) return null;
    const [x0, y0] = outlineXY[0];
    const [x1, y1] = outlineXY[1];
    const tx = x1 - x0;
    const ty = y1 - y0;
    const len = Math.hypot(tx, ty) || 1;
    // unit perpendicular
    const px = -ty / len;
    const py = tx / len;
    const half = dotRadius * 1.8; // half-length of the line on each side of the racing line
    return {
      x1: x0 + px * half,
      y1: y0 + py * half,
      x2: x0 - px * half,
      y2: y0 - py * half,
    };
  }, [outlineXY, dotRadius]);

  // Corner numbers should be readable but not dominate the dots.
  const cornerFontSize = dotRadius * 1.3;

  // Label rendering knobs.
  const labelFontSize = dotRadius * 1.8;
  // Distance from dot center to label baseline. Far enough that the
  // halo'd text sits clearly above the dot rather than touching it.
  const labelOffset = dotRadius * 3.6;

  // ── driver list (metadata only — positions come from refs/RAF) ───────
  // Filter out "phantom" drivers: reserve/test drivers who are listed in
  // DriverList but not actually on track. They show up with position=null
  // and x=y=0, which projects onto some arbitrary spot on the outline.
  const driverList = useMemo(() => {
    if (!drivers) return [];
    return drivers
      .filter(d =>
        d.x != null && d.y != null &&
        !(d.x === 0 && d.y === 0) &&
        d.position != null
      )
      .map((d, i) => ({
        number: d.number,
        tla: d.tla || d.number,
        color: seriesColor(d, i),
        inPit: d.in_pit,
        retired: d.retired || d.stopped,
        position: d.position,
      }));
  }, [drivers]);

  // ── interpolation buffer ─────────────────────────────────────────────
  // Per-driver ring buffer of recent (arc, sample-timestamp) entries.
  // Timestamps are the F1-reported sample times (ms since Unix epoch)
  // sent in drivers[i].pos_history by the backend. Driving the buffer
  // off sample timestamps lets us ingest ALL position samples from each
  // Position.z message (not just the latest), so motion is truly
  // continuous between snapshot pushes.
  const bufferRef = useRef({}); // num -> [{arc, t}, ...] sorted by t ascending
  // Clock sync: last-known sample timestamp + wall time when it arrived.
  // The RAF loop uses this to convert wall clock → F1 sample time, so
  // rendering advances smoothly between snapshot arrivals.
  const sampleClockRef = useRef({ lastSampleT: 0, lastWallT: 0 });
  // element refs per driver
  const circleRefs = useRef({});
  const labelRefs = useRef({});
  const rafRef = useRef(null);

  // On snapshot update: ingest each driver's pos_history into its buffer.
  // pos_history is an array of {t, x, y} where t is ms since Unix epoch.
  // We dedupe by timestamp (older/equal timestamps are already in buffer).
  useEffect(() => {
    if (!arcIndex || !drivers) return;
    let maxSampleT = sampleClockRef.current.lastSampleT;

    for (const d of drivers) {
      const hist = d.pos_history;
      if (!hist || hist.length === 0) {
        // Fallback: if the backend didn't ship history for this driver,
        // seed the buffer with the current (x, y) so the dot still appears.
        if (d.x == null || d.y == null) continue;
        if (!bufferRef.current[d.number]) bufferRef.current[d.number] = [];
        const buf = bufferRef.current[d.number];
        if (buf.length === 0) {
          const [rx, ry] = rotate(d.x, d.y, rot);
          const arc = projectToArclength(arcIndex, rx, -ry);
          buf.push({ arc, t: Date.now() });
        }
        continue;
      }

      if (!bufferRef.current[d.number]) bufferRef.current[d.number] = [];
      const buf = bufferRef.current[d.number];
      const lastT = buf.length > 0 ? buf[buf.length - 1].t : -Infinity;

      for (const sample of hist) {
        if (sample.t <= lastT) continue; // already have this or older
        const [rx, ry] = rotate(sample.x, sample.y, rot);
        const arc = projectToArclength(arcIndex, rx, -ry);
        buf.push({ arc, t: sample.t });
        if (sample.t > maxSampleT) maxSampleT = sample.t;
      }
      while (buf.length > BUFFER_MAX) buf.shift();
    }

    if (maxSampleT > sampleClockRef.current.lastSampleT) {
      sampleClockRef.current = {
        lastSampleT: maxSampleT,
        lastWallT: performance.now(),
      };
    }
  }, [drivers, arcIndex, rot]);

  // Keep the selected prop in a ref so the RAF loop (which has a stable
  // effect dep list) always sees the latest selection without having to
  // re-bind its rAF callback.
  const selectedRef = useRef(selected);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  // RAF loop: 60 Hz, directly mutates SVG element attributes.
  //
  // Label placement is the simplest possible: label sits at a fixed
  // offset above each dot, always. Labels are visibility-gated at
  // render time (only selected/hovered drivers render their <text>
  // element in the first place), so there's no crowding to resolve.
  useEffect(() => {
    if (!arcIndex) return;

    const tick = () => {
      const now = performance.now();
      const totalLen = arcIndex.totalLen;
      const buffers = bufferRef.current;

      const { lastSampleT, lastWallT } = sampleClockRef.current;
      if (lastSampleT === 0) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const renderTime = lastSampleT - RENDER_LAG_MS + (now - lastWallT);

      for (const num in buffers) {
        const buf = buffers[num];
        if (!buf || buf.length === 0) continue;

        let arc;
        if (buf.length === 1) {
          arc = buf[0].arc;
        } else if (renderTime <= buf[0].t) {
          arc = buf[0].arc;
        } else if (renderTime >= buf[buf.length - 1].t) {
          arc = extrapolateArc(buf, renderTime, totalLen);
        } else {
          let a = buf[0];
          let b = buf[buf.length - 1];
          for (let i = 0; i < buf.length - 1; i++) {
            if (buf[i].t <= renderTime && buf[i + 1].t > renderTime) {
              a = buf[i];
              b = buf[i + 1];
              break;
            }
          }
          const alpha = (renderTime - a.t) / Math.max(1, b.t - a.t);
          arc = arcLerp(a.arc, b.arc, alpha, totalLen);
        }

        const [x, y] = arclengthToXY(arcIndex, arc);

        const circle = circleRefs.current[num];
        if (circle) {
          circle.setAttribute('cx', x);
          circle.setAttribute('cy', y);
        }
        const label = labelRefs.current[num];
        if (label) {
          label.setAttribute('x', x);
          label.setAttribute('y', y - labelOffset);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [arcIndex, labelOffset]);

  // Clean up refs for drivers that disappeared
  useEffect(() => {
    const numbers = new Set(driverList.map(d => d.number));
    for (const num of Object.keys(circleRefs.current)) {
      if (!numbers.has(num)) delete circleRefs.current[num];
    }
    for (const num of Object.keys(labelRefs.current)) {
      if (!numbers.has(num)) delete labelRefs.current[num];
    }
    for (const num of Object.keys(bufferRef.current)) {
      if (!numbers.has(num)) delete bufferRef.current[num];
    }
  }, [driverList]);

  if (!outline || outline.length === 0) {
    return (
      <div className={styles.trackMapWrap}>
        <span style={{ fontFamily: "var(--mono)", fontSize: '0.65rem', color: '#8c8c8c' }}>
          loading track outline…
        </span>
      </div>
    );
  }

  return (
    <div className={styles.trackMapWrap}>
      <svg className={styles.trackSvg} viewBox={viewBox} preserveAspectRatio="xMidYMid meet">
        <path d={pathD} className={styles.trackOutline} />
        <path d={pathD} className={styles.trackCenter} />

        {/* Start/finish line */}
        {startFinish && (
          <line
            x1={startFinish.x1}
            y1={startFinish.y1}
            x2={startFinish.x2}
            y2={startFinish.y2}
            stroke="#ffffff"
            strokeWidth={dotRadius * 0.35}
            strokeLinecap="butt"
            opacity={0.9}
          />
        )}

        {cornerPts.map(c => (
          <text
            key={`c${c.number}`}
            x={c.x}
            y={c.y}
            className={styles.cornerLabel}
            style={{ fontSize: cornerFontSize }}
          >
            {c.number}
          </text>
        ))}

        {/* Dots + labels — labels only render when the driver is
            selected (telemetry) or hovered (here or in the timing
            table). Keeps the map clean when no specific driver is
            being tracked. */}
        {driverList.map(d => {
          const isSel = selected?.includes(d.number);
          const isHov = hovered === d.number;
          const showLabel = isSel || isHov;
          return (
            <g
              key={d.number}
              onClick={() => onToggle?.(d.number)}
              onPointerEnter={() => onHover?.(d.number)}
              onPointerLeave={() => onHover?.(null)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                ref={el => { if (el) circleRefs.current[d.number] = el; }}
                r={dotRadius}
                fill={d.retired ? RETIRED_FILL : d.color}
                stroke={isSel ? '#fff' : '#000'}
                strokeWidth={isSel ? dotRadius * 0.25 : dotRadius * 0.12}
                className={styles.driverDot}
                opacity={d.inPit ? 0.4 : 1}
              />
              {showLabel && (
                <text
                  ref={el => {
                    if (!el) return;
                    labelRefs.current[d.number] = el;
                    // Initial sync from the live circle so we don't
                    // flash at (0,0) for the first frame after mount.
                    const c = circleRefs.current[d.number];
                    if (c) {
                      const cx = parseFloat(c.getAttribute('cx')) || 0;
                      const cy = parseFloat(c.getAttribute('cy')) || 0;
                      el.setAttribute('x', cx);
                      el.setAttribute('y', cy - labelOffset);
                    }
                  }}
                  className={styles.driverLabel}
                  style={{ fontSize: labelFontSize }}
                >
                  {d.tla}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
